import axios from 'axios'

const STORAGE_KEY = 'student-support.api-key'

// Dev: BASE_URL is "/" and Vite proxies /api to the backend.
// Prod: BASE_URL is "/support/" and nginx proxies /support/api to the backend.
const baseURL = import.meta.env.VITE_API_URL || `${import.meta.env.BASE_URL}api`

export const api = axios.create({ baseURL, timeout: 30000 })

/** UTF-8 safe base64, so non-ASCII passwords survive the round trip. */
function toBase64(value) {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return window.btoa(binary)
}

export function basicAuth(secret) {
  return `Basic ${toBase64(`:${secret}`)}`
}

/* The unlock key lives in localStorage rather than a 10-year cookie: it is not
   needed by the server on navigation, and it is trivially clearable on a shared
   kiosk. Wrapped because Safari private mode throws on access. */
export function loadKey() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

export function saveKey(key) {
  try {
    window.localStorage.setItem(STORAGE_KEY, key)
  } catch {
    /* non-persistent session is still usable */
  }
}

export function clearKey() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** Attaches the unlock key to every call that is not the sign-in itself. */
api.interceptors.request.use((request) => {
  if (!request.skipAuth) {
    const key = loadKey()
    if (key) request.headers.Authorization = basicAuth(key)
  }
  return request
})

/** The API answers errors as { error: "..." }; prefer that over a generic string. */
export function errorMessage(error, fallback) {
  const fromServer = error?.response?.data?.error
  return typeof fromServer === 'string' && fromServer ? fromServer : fallback
}

/** True when the stored key was rejected and the kiosk must be unlocked again. */
export function isAuthError(error) {
  return error?.response?.status === 401
}
